<template>
  <div class="mb-15">
    <div class="mb-4">
      <v-btn color="grey" variant="text"to="/mail/inbox"> Back to List </v-btn>
    </div>
    <div class="d-flex align-center">
      <h2 v-if="isDraft">Edit Draft</h2>
      <h2 v-else>New Message</h2>
      <v-spacer />
      <v-btn
        @click="templateDialog = true"
        :disabled="loading"
        color="primary"
        variant="outlined"
        >Template</v-btn
      >
    </div>

    <v-form v-model="valid" ref="form">
      <v-container class="pt-6">
        <v-row>
          <v-col>
            <v-text-field
              v-model="title"
              :rules="[required]"
              :disabled="loading"
              label="Title"
              variant="filled" >
            </v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-radio-group v-model="targetVariant" inline>
            <v-radio
              label="Broadcast to Class/Group"
              value="group"
              :disabled="loading"
            ></v-radio>
            <v-radio label="Select children to broadcast" :disabled="loading" value="children"></v-radio>
          </v-radio-group>
        </v-row>

        <v-row>
          <v-col cols="3">
            <h5>{{ $t('common.fiscalYear') }}</h5>
            <v-select
              v-model="searchFilters.year"
              :items="yearList"
              :disabled="loading"
              variant="outlined"
            >
            </v-select>
          </v-col>
        </v-row>
        <v-row v-if="targetVariant === 'group'">
          <v-col cols="6">
            <v-select
              v-model="classIds"
              :items="classList"
              item-title="className"
              item-value="classId"
              :disabled="loading"
              multiple
              label="To (Class)"
              variant="outlined"
            >
            </v-select>
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="groupIds"
              :items="groups"
              item-title="name"
              item-value="groupId"
              :disabled="loading"
              multiple
              label="To (Group)"
              variant="outlined"
            >
            </v-select>
          </v-col>
        </v-row>

        <div v-if="targetVariant === 'children'">
          <h4 class="mt-8 mb-4">Select Children</h4>
          <v-row>
            <v-col cols="6">
              <v-select
                v-model="childrenSelectionClassIds" 
                :items="classList"
                item-title="className"
                item-value="classId"
                :disabled="loading"
                multiple
                label="Add children from class"
                variant="outlined"
              >
              </v-select>
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="childrenSelectionGroupIds"
                :items="groups"
                item-title="name"
                item-value="groupId"
                :disabled="loading"
                multiple
                label="Add children from group"
                variant="outlined"
              >
              </v-select>
            </v-col>
          </v-row>
          <div>
            <v-progress-linear
              v-if="isFetchingChildren"
              indeterminate
              color="primary"
              class="my-8"
            ></v-progress-linear>
          </div>
          <v-row>
            <v-col cols="6">
              <h5>Select Individually</h5>
              <div>
                <v-card
                  variant="outlined"
                  class="pa-2 mb-2"
                  v-for="child in childrenOptions"
                  :key="child.classId"
                >
                  <div class="d-flex align-center">
                    <div>{{ child.lastName }} {{ child.firstName }}</div>
                    <v-spacer></v-spacer>
                    <div>
                      <v-btn
                        :disabled="loading"
                        variant="outlined"
                        size="small" color="primary"
                        @click="selectChild(child)"
                        >Select</v-btn
                      >
                    </div>
                  </div>
                </v-card>
              </div>
            </v-col>
            <v-col cols="6">
              <h5>Selected Children</h5>
              <div>
                <v-card
                  variant="outlined"
                  class="pa-2 mb-2"
                  v-for="child in selectedChildren"
                  :key="child.classId"
                >
                  <div class="d-flex align-center">
                    <div>{{ child.lastName }} {{ child.firstName }}</div>
                    <v-spacer></v-spacer>
                    <div>
                      <v-btn variant="outlined" size="small" @click="removeChild(child)"
                        :disabled="loading"
                        >Deselect</v-btn
                      >
                    </div>
                  </div>
                </v-card>
              </div>
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-8"></v-divider>

        <v-row>
          <v-col>
            <v-textarea
              v-model="body"
              :rules="[required]"
              :disabled="loading"
              label="Message Body"
              height="30vh"
              variant="filled" >
            </v-textarea>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-file-input
              v-model="file"
              label="Select files (multiple allowed)"
              suffix="PDF/Images"
              :disabled="loading"
              multiple
              variant="outlined"
              accept="application/pdf, image/png, image/jpeg, image/jpg"
            >
            </v-file-input>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
                <v-row>
                  <v-col cols="12" v-for="(item, i) in attachmentUrlsDraft" :key="i" class="py-2">
                    <v-img :src="attachmentUrlsDraft[i]"></v-img>
                    <v-btn
                      :href="item"
                      target="_blank"
                      color="primary"
                      variant="outlined"
                      size="small" >
                      {{ `Attachment ${Number(i)+1}` }}
                    </v-btn>
                    &emsp;<v-btn
                      @click="deleteDownloadUrl(i)"
                      color="primary"
                      variant="flat"
                      size="small" >
                      {{ `Delete` }}
                    </v-btn>
                  </v-col>
                </v-row>
          </v-col>
        </v-row>

        <v-row justify="center">
          <v-col cols="3">
            <v-btn
              @click="deleteChat"
              :loading="loading"
              color="red"
              dark
              variant="flat"
              block
            >
              {{ $t('common.delete') }}
            </v-btn>
          </v-col>
          <v-col cols="3">
            <v-btn
              @click="saveAsTemplate"
              :loading="loading"
              :disabled="!valid"
              color="blue-grey"
              variant="outlined"
              block
              >Save as Template
            </v-btn>
          </v-col>
          <v-col cols="3">
            <v-btn
              @click="saveAsDraft"
              :loading="loading"
              :disabled="!valid"
              color="blue-grey"
              class="white--text"
              variant="flat"
              block
            >
              Save Draft
            </v-btn>
          </v-col>
          <v-col cols="3">
            <v-btn
              @click="send"
              :disabled="!valid"
              :loading="loading"
              color="primary"
              variant="flat"
              block
            >
              Send
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-form>

    <v-dialog v-model="templateDialog" max-width="700px">
      <v-card>
        <v-card-title>Templates</v-card-title>
        <v-card-text>
          <v-container>
            <v-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Body</th>
                    <th></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in templates" :key="i">
                    <td>{{ item.title }}</td>
                    <td>{{ item.body }}</td>
                    <td>
                      <v-btn
                        @click="applyTemplate(item.templateId)"
                        color="primary"
                        variant="flat"
                        size="small" >Apply</v-btn
                      >
                    </td>
                    <td>
                      <v-btn
                        @click="deleteTemplate(item.templateId)"
                        color="error"
                        variant="outlined"
                        size="small" >{{ $t('common.delete') }}</v-btn
                      >
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-table>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-container>
            <v-row>
              <v-btn @click="templateDialog = false" color="grey" variant="text" >{{ $t('common.cancel') }}
              </v-btn>
              <v-spacer />
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import { chatEditModule } from "@/store/dataModules/chatEditModule";
import { classModule } from "@/store/dataModules/classModule";
import { NurseryClassResponse } from "chaild-api/lib";
import LocalDataService from "@/service/LocalDataService";
import ValidationMixin from "@/mixins/ValidationMixin";
import Swal from "sweetalert2";
import ApiResource from "@/api/ApiResource";
import {
  GroupChatTemplateResponse,
  NurseryChildResponse,
} from "chaild-api/lib";
import ApiChat from "@/api/ApiChat";
import { chatModule } from "@/store/dataModules/chatModule";
import { groupModule } from "@/store/dataModules/groupModule";
import dayjs from "dayjs";

interface SearchFilters {
  year: number | string;
}
interface ComponentData {
  searchFilters: SearchFilters;
  valid: boolean;
  file: File[];
  loading: boolean;
  templateDialog: boolean;
  isFetchingChildren: boolean;
}
export default defineComponent({
  name: "NewMessage",

  mixins: [ValidationMixin],

  async mounted() {
    await this.listClasses();
    await this.listGroups();
    await this.listTemplates();

    // 新規作成の場合
    if (!this.isDraft) {
      chatEditModule.setTitle("");
      chatEditModule.setBody("");
      chatEditModule.setClassIds([]);
      chatEditModule.setGroupIds([]);
      chatEditModule.setAttachmentUrls([]);
      chatEditModule.setAttachmentDownloadUrls([]);

      // reset validation
      (this.$refs.form as any).reset();
    }

    // 年度を設定する
    let currentYear = dayjs().year();
    const currentMonth = dayjs().month() + 1;
    if (currentMonth < 8) {
      currentYear -= 1;
    }
    this.searchFilters.year = Number(currentYear);
  },
  data(): ComponentData {
    return {
      searchFilters: {
        year: 0,
      },
      valid: false,
      file: [] as File[],
      loading: true,
      templateDialog: false,
      isFetchingChildren: false,
    };
  },

  computed: {
    attachmentUrlsDraft: {
      get() {
        return chatEditModule.attachmentUrls;
      },
    },
    attachmentDownloadUrlsDraft: {
      get() {
        return chatEditModule.attachmentDownloadUrls;
      },
    },
    body: {
      get() {
        return chatEditModule.body;
      },
      set(value: string) {
        chatEditModule.setBody(value);
      },
    },
    title: {
      get() {
        return chatEditModule.title;
      },
      set(value: string) {
        chatEditModule.setTitle(value);
      },
    },
    classIds: {
      get() {
        return chatEditModule.classIds;
      },
      set(value: number[]) {
        chatEditModule.setClassIds(value);
      },
    },
    groupIds: {
      get() {
        return chatEditModule.groupIds;
      },
      set(value: number[]) {
        chatEditModule.setGroupIds(value);
      },
    },
    childrenSelectionClassIds: {
      get() {
        return chatEditModule.childrenSelectionClassIds
      },
      set(ids: number[]) {
        const oldValue = [...chatEditModule.childrenSelectionClassIds];
        chatEditModule.setChildrenSelectionClassIds(ids)

        if (ids.length > oldValue.length) {
          const newValues = ids.filter((id) => {
            return oldValue.indexOf(id) < 0;
          });

          (this as any).addChildrenWithClassIds(newValues);
        } else {
          const removeValues = oldValue.filter((id) => {
            return ids.indexOf(id) < 0;
          });

          (this as any).removeChildrenWithClassId(removeValues);
        }
      }
    },
    childrenSelectionGroupIds: {
      get() {
        return chatEditModule.childrenSelectionGroupIds
      },
      set(ids: number[]) {
        const oldValue = [...chatEditModule.childrenSelectionGroupIds];
        chatEditModule.setChildrenSelectionGroupIds(ids)

        if (ids.length > oldValue.length) {
          const newValues = ids.filter((id) => {
            return oldValue.indexOf(id) < 0;
          });

          (this as any).addChildrenWithGroupIds(newValues);
        } else {
          const removeValues = oldValue.filter((id) => {
            return ids.indexOf(id) < 0;
          });

          (this as any).removeChildrenWithGroupdId(removeValues);
        }
      }
    },
    targetVariant: {
      get() {
        return chatEditModule.targetVariant;
      },
      set(value: "group" | "children") {
        chatEditModule.setTargetVariant(value);
      },
    },
    selectedChildren() {
      return chatEditModule.selectedChildren;
    },
    childrenOptions() {
      return chatEditModule.childrenOptions;
    },
    classList() {
      const self = this as unknown as { searchFilters: SearchFilters; classes: NurseryClassResponse[] }; // 型キャスト
      const targetYear = Number(self.searchFilters.year);
      if (isNaN(targetYear)) {
        console.warn("Invalid year value:", self.searchFilters.year);
        return []; // 空配列を返す
      }
      return classModule.classes
        .filter((c: NurseryClassResponse) => Number(c?.year) == targetYear).map((c) => {
        return {
          className: `SY${c.year} ${c.className}`,
          classId: c.classId,
        };
      });
    },
    yearList() {
      // 安全対策：データがまだ取得できていない時は空の配列を返す
      if (!classModule.classes || classModule.classes.length === 0) {
        return [];
      }

      // 全クラスから「年(year)」だけを抽出する（ここまでは元のコードと同じ）
      const allYears = classModule.classes.map((c) => c.year);

      // 超重要：Setを使って「重複」を削除する！（例: [2023, 2023, 2024] -> [2023, 2024]）
      const uniqueYears = [...new Set(allYears)];

      // UX向上：新しい年が一番上に来るように降順でソートする
      return uniqueYears.sort((a, b) => Number(b) - Number(a));
    },
    isDraft: function () {
      if ((this as any).$route.query.id) {
        return true;
      } else {
        return false;
      }
    },
    templates: function (): GroupChatTemplateResponse[] {
      return chatModule.templates;
    },
    groups: function (): any[] {
      return groupModule.groups.map((g) => {
        return {
          name: `SY${g.year} ${g.name}`,
          groupId: g.groupId,
        };
      });
    },
  },

  methods: {
    deleteDownloadUrl(index) {
      chatEditModule.removeAttachmentUrl(index);
    },
    async send() {
      (this as any).loading = true;
      const attachmentUrls = await this.getAttachmentUrls();
      chatEditModule.setAttachmentUrls(attachmentUrls);
      const response = await chatEditModule.sendGroupChat();
      if (response) {
        Swal.fire({ title: "Message sent", icon: "success" }).then(() => {
          (this as any).$router.push({ path: "/mail/inbox" });
        });
      } else {
        Swal.fire({ title: "Failed to send", icon: "error" });
      }
      (this as any).loading = false;
    },
    async saveAsDraft() {
      (this as any).loading = true;

      const groupChatId = (this as any).$route.query.id;
      const attachmentUrls = await this.getAttachmentUrls();
      chatEditModule.setAttachmentUrls(attachmentUrls);
      const response = await chatEditModule.saveAsDraft({
        groupChatId: groupChatId ? String(groupChatId) : undefined,
      });
      if (response) {
        (this as any).$router.push({ path: "/mail/inbox" });
      }
      (this as any).loading = false;
    },
    async listClasses() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await classModule.listClass({
          nurseryId: nurseryId,
        });
      }
    },
    async getAttachmentUrls() {
      const resultUrls: string[] = [];
      const nurseryId = LocalDataService.getNurseryId();
      if (!nurseryId) return resultUrls;

      const itemsToProcess: (File | string)[] = [...(this as any).file, ...(this as any).attachmentDownloadUrlsDraft];

      for (const item of itemsToProcess) {
        let fileToUpload: File;

        if (typeof item === 'string') {
          try {
            fileToUpload = await this.urlToFile(item);
          } catch (error) {
            console.error("URLからの取得に失敗しました:", item);
            continue;
          }
        } else {
          fileToUpload = item;
        }

        const presignedUrl = await ApiResource.getPresignedUrl({
          imageType: "chat",
          filename: fileToUpload.name,
          nurseryId: nurseryId,
        }).catch(() => {
          (this as any).loading = false;
          return null;
        });

        if (presignedUrl) {
          const uploadResponse = await ApiResource.uploadFile({
            url: presignedUrl.url,
            file: fileToUpload,
          }).catch(() => {
            (this as any).loading = false;
            return null;
          });

          if (uploadResponse) {
            const url = presignedUrl.url.split("?")[0];
            resultUrls.push(url);
          }
        }
      }
      return resultUrls;
    },

    async urlToFile(url: string): Promise<File> {
      try {
        const response = await fetch(url, {
          method: 'GET',
          mode: 'cors', // CORSポリシーに従う
        });

        if (!response.ok) {
          throw new Error(`ネットワークレスポンスが正常ではありません (status: ${response.status})`);
        }

        const blob = await response.blob();

        const filename = url.split('/').pop()?.split('?')[0] || 'downloaded_file';

        return new File([blob], filename, { type: blob.type });
      } catch (error) {
        console.error("urlToFile conversion failed:", error);
        throw error;
      }
    },

    async deleteChat() {
      (this as any).loading = true;

      const groupChatId = (this as any).$route.query.id;
      if (groupChatId) {
        // 下書きを削除
        const response = await ApiChat.deleteDraftGroupChat({
          groupChatIds: String(groupChatId),
        });
        if (response) {
          (this as any).$router.push({ path: "/mail/inbox" });
        }
      } else {
        // 戻るだけ
        (this as any).$router.push({ path: "/mail/inbox" });
      }
      (this as any).loading = false;
    },
    async listTemplates() {
      (this as any).loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await chatModule
          .listTemplates({
            nurseryId,
          })
          .catch();
      }
      (this as any).loading = false;
    },
    async saveAsTemplate() {
      (this as any).loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        if ((this as any).targetVariant === "group") {
          const response = await ApiChat.createTemplate({
            nurseryId,
            title: chatEditModule.title,
            classIds: chatEditModule.classIds,
            groupIds: chatEditModule.groupIds,
            body: chatEditModule.body,
            attachmentUrls: [],
          }).catch();
          if (response) {
            alert("Saved successfully");
          }
        } else {
          const childIds = chatEditModule.selectedChildren.map(
            (c) => c.childId
          );
          const response = await ApiChat.createTemplate({
            nurseryId,
            title: chatEditModule.title,
            childIds,
            body: chatEditModule.body,
            attachmentUrls: [],
          }).catch();
          if (response) {
            alert("Saved successfully");
          }
        }
      }
      (this as any).loading = false;
    },
    async applyTemplate(templateId: string) {
      (this as any).loading = true;
      if (templateId) {
        const response = (await ApiChat.getTemplate({
          templateId,
        }).catch()) as GroupChatTemplateResponse;
        if (response) {
          if (response.toClasses.length > 0 || response.toGroups.length > 0) {
            const classIds = response.toClasses.map((r) => {
              return r.classId;
            });
            const groupIds = response.toGroups.map((g) => {
              return g.groupId;
            });
            chatEditModule.setTitle(response.title || "");
            chatEditModule.setBody(response.body);
            chatEditModule.setClassIds(classIds);
            chatEditModule.setGroupIds(groupIds);
            chatEditModule.setTargetVariant("group");
          } else {
            chatEditModule.setTitle(response.title || "");
            chatEditModule.setBody(response.body);
            chatEditModule.setSelectedChildren(response.toChildren);
            chatEditModule.setTargetVariant("children");
          }
        }
        (this as any).templateDialog = false;
      }
      (this as any).loading = false;
    },
    async deleteTemplate(templateId: string) {
      (this as any).loading = true;
      if (templateId) {
        const response = await ApiChat.deleteTemplate({
          templateId,
        }).catch();
        if (response) {
          alert("Template deleted");
          (this as any).templateDialog = false;
        }
      }
      (this as any).loading = false;
    },
    async listGroups() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        (this as any).loading = true;
        await groupModule.listGroups({
          nurseryId,
        });
        (this as any).loading = false;
      }
    },
    async addChildrenWithClassIds(ids: number[]) {
      (this as any).isFetchingChildren = true;
      await Promise.all(
        ids.map((id) => chatEditModule.addChildrenWithClassIds(id))
      );
      (this as any).isFetchingChildren = false;
    },
    async removeChildrenWithClassId(ids: number[]) {
      (this as any).isFetchingChildren = true;
      await Promise.all(ids.map((id) => chatEditModule.removeChildrenWithClassId(id)));
      (this as any).isFetchingChildren = false;
    },
    async addChildrenWithGroupIds(ids: number[]) {
      (this as any).isFetchingChildren = true;
      await Promise.all(
        ids.map((id) => chatEditModule.addChildrenWithGroupId(id))
      );
      (this as any).isFetchingChildren = false;
    },
    async removeChildrenWithGroupdId(ids: number[]) {
      (this as any).isFetchingChildren = true;
      await Promise.all(ids.map((id) => chatEditModule.removeChildrenWithGroupId(id)));
      (this as any).isFetchingChildren = false;
    },
    selectChild(child: NurseryChildResponse) {
      chatEditModule.addSelectedChildren(child);
    },
    removeChild(child: NurseryChildResponse) {
      chatEditModule.removeChild(child.childId);
    },
  },
} as any);
</script>
